import { ColumnDef } from "@tanstack/react-table";
import { AxiosError } from "axios";
import React, { useEffect, useState, useRef } from "react";
import DataTable2 from "shared/DataTable2";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import {
  ServerDeletedFilterValue,
  ServerStatusFilterValue,
} from "../../shared/ServerListFilters";
import ServerStatusPills from "../../shared/ServerStatusPills";
import ServerDeletedPills from "../../shared/ServerDeletedPills";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { policy, Result } from "../../interface/Policy";
import { PolicyAxiosInstance } from "../../Network/PolicyAxiosInstance";
import FileUpload from "Pages/uploadFile/Policy/Policy";
import PolciyNetworksSelector from "Pages/Shared/PolciyNetworks";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import PolciyPlansSelector from "Pages/Shared/PolciyPlan";
import { Option } from "interface/Option";
import useQaAutomationIds from "../../utils/useQaAutomationIds";

// ─── Role Helper ──────────────────────────────────────────────────────────────
const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};

const Policy: React.FC = () => {
  useQaAutomationIds("policy");
  const [items, setItems] = useState<policy>({ result: [] });

  // Form state
  const [selectedPlans, setSelectedPlans] = useState<Option[]>([]);
  const [selectedNetworks, setSelectedNetworks] = useState<Option[]>([]);
  const [plansOptions, setPlansOptions] = useState<{ id: string, name: string }[]>([]);
  const [networksOptions, setNetworksOptions] = useState<{ id: string, name: string }[]>([]);
  const [policyName, setPolicyName] = useState<string>("");
  const [policyId, setPolicyId] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [plan, setPlan] = useState<string[]>([]);
  const [network, setNetwork] = useState<string[]>([]);
  const [filterName, setFilterName] = useState("");
  const [filterStatus, setFilterStatus] = useState<ServerStatusFilterValue>("");
  const [filterDeleted, setFilterDeleted] = useState<ServerDeletedFilterValue>("false");
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});
  const [isVIP, setIsVIP] = useState<boolean>(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [networkDialogOpen, setNetworkDialogOpen] = useState(false);
  const [clearNetworks, setClearNetworks] = useState(false);
  const [networks, setNetworks] = useState<Option[]>([]);
  const [clearPlans, setClearPlans] = useState(false);
  const [plans, setPlans] = useState<Option[]>([]);
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  // ─── Force Dialog state ────────────────────────────────────────────────────
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceTargetItem, setForceTargetItem] = useState<Result | null>(null);
  const [forceError, setForceError] = useState<string>("");

  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();

  // ✅ صلاحية الريستور والهارد دليت
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => { if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id); });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  const clear = () => {
    setPolicyName(""); setPolicyId(""); setDescription("");
    setIsActive(true); setStartDate(""); setEndDate("");
    setPlan([]); setNetwork([]); setIsVIP(false);
    setSelectedPlans([]); setSelectedNetworks([]);
    setPlans([]); setNetworks([]);
    setPlanError(""); setNetworkError("");
    setClearPlans((value) => !value);
    setClearNetworks((value) => !value);
  };

  const handleAddClick = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };
  const handleEditClick = (item: Result) => { handleEdit(item); setIsDialogOpen(true); };

  // ✅ Fetch — أحدث أول + المحذوفين آخر
  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Result, {
    name: string;
    active: ServerStatusFilterValue;
    deleted: ServerDeletedFilterValue;
  }>({
    filters: { name: filterName, active: filterStatus, deleted: filterDeleted },
    fetchPage: ({ page, limit, filters }) =>
      PolicyAxiosInstance.get("getpolicy", {
        params: {
          page,
          limit,
          name: filters.name.trim() || undefined,
          active: filters.active || undefined,
          is_deleted: filters.deleted,
        },
      }),
    setRows: (rows) => setItems({ result: rows }),
    normalizeRow: (item: any) => ({
      ...item,
      status:
        item.status === true || item.status === "true" || item.status === "Active"
          ? "Active"
          : "Inactive",
    }),
    sortRows: (rows) =>
      [...rows]
        .sort((a: any, b: any) => Number(b.id) - Number(a.id))
        .sort((a: any, b: any) =>
          a.is_deleted === b.is_deleted ? 0 : a.is_deleted ? 1 : -1
        ),
    onError: (error) => {
      console.error("Error fetching items:", error);
      toast({ title: "Error", description: "Failed to fetch Policy", variant: "destructive" });
    },
  });

  // ✅ Status Toggle — with Force Dialog support
  const togglePolicyStatus = async (item: Result, force = false): Promise<void> => {
    try {
      const currentStatus = item.status === "Active" || item.status === true;
      const ValuesNetworks = Array.isArray(item.network) ? item.network.map((n: any) => n.network_name || n) : [];
      const ValuesPlans = Array.isArray(item.plan) ? item.plan.map((p: any) => p.plan_name || p.name || p) : [];
      const url = force ? `updatepolicy?force=true` : `updatepolicy`;
      const updatedPolicy = {
        Policy_name: item.policy_name, Policy_id: item.policy_id,
        Status: !currentStatus ? "Active" : "Inactive",
        Start_date: item.start_date, End_date: item.end_date,
        Plan: ValuesPlans, Network: ValuesNetworks, token, id: item.id,
      };
      const response = await PolicyAxiosInstance.put(url, updatedPolicy);
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceTargetItem(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: `Status changed to ${!currentStatus ? "Active" : "Inactive"}.` });
      } else {
        setForceId(item.id); setForceTargetItem(item); setForceAction("status");
        setForceError(response.data.error || "This policy is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "An unexpected error occurred.";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(item.id); setForceTargetItem(item); setForceAction("status");
        setForceError(errMsg); setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  const addItem = async (): Promise<void> => {
    setNameError(""); setIdError(""); setDateError("");
    setPlanError(""); setNetworkError("");
    if (!policyName.trim() || !startDate || !endDate) {
      if (!policyName.trim()) setNameError("Name required");
      if (startDate > endDate) { setDateError("End Date must be after Start Date."); return; }
      return;
    }
    if (selectedPlans.length > 1) {
      setPlanError("Each policy can only have one plan.");
      return;
    }
    if (selectedNetworks.length > 1) {
      setNetworkError("Each policy can only have one network.");
      return;
    }
    if (nameError || idError || dateError) return;
    try {
      const ValuesNetworks = selectedNetworks.length === 1
        ? [selectedNetworks[0].label]
        : [];
      const ValuesPlans = selectedPlans.length === 1
        ? [selectedPlans[0].label]
        : [];
      const newPolicy = {
        Policy_name: policyName, Policy_id: policyId,
        Status: isActive ? "Active" : "Inactive",
        Start_date: startDate, End_date: endDate,
        Plan: ValuesPlans, Network: ValuesNetworks, token,
      };
      const response = await PolicyAxiosInstance.post("addpolicy", newPolicy);
      if (response.data.success) {
        await fetchItems(); setCurrentPage(0); clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "New Policy Added Successfully." });
      } else {
        const errorMessage = getPolicyErrorMessage({
          response: { data: response.data },
        });
        applyPolicyRelationError(errorMessage);
        toast({ title: "Error", description: errorMessage || "Failed to add Policy.", variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      const errorMessage = getPolicyErrorMessage(error);
      applyPolicyRelationError(errorMessage);
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
    }
  };

  const handleEdit = (item: Result) => {
    setPolicyName(item.policy_name); setPolicyId(item.policy_id);
    setDescription(item.description || "");
    setIsActive(item.status === "Active" || item.status === true);
    setStartDate(item.start_date ? item.start_date.split("T")[0] : "");
    setEndDate(item.end_date ? item.end_date.split("T")[0] : "");
    const networksData = Array.isArray(item.network)
      ? item.network.map((obj: any) => typeof obj === "string" ? { id: 0, network_name: obj } : obj) : [];
    const normalizedSelectedNetworks = networksData.map((obj: any) => ({
      value: obj.id ?? obj.network_name,
      label: obj.network_name ?? obj.name ?? String(obj.value ?? ""),
    }));
    setSelectedNetworks(normalizedSelectedNetworks); setNetworks(normalizedSelectedNetworks);
    const plansData = Array.isArray(item.plan) ? item.plan : [];
    const normalizedPlans = plansData.map((p: any) => ({
      value: String(p.id ?? p.plan_id ?? p.plan_name ?? ""),
      label: p.plan_name ?? p.Plan_name ?? p.name ?? String(p),
    }));
    setSelectedPlans(normalizedPlans); setPlans(normalizedPlans);
    setEditIndex(item.id); setIsDialogOpen(true);
  };

  const handleNetwrksChange = (selected: Option[]) => {
    if (selected.length > 1) {
      const firstNetwork = selected.slice(0, 1);
      setSelectedNetworks(firstNetwork);
      setNetworks(firstNetwork);
      setNetworkError("Each policy can only have one network.");
      toast({
        title: "Each policy can only have one network.",
        variant: "destructive",
      });
      return;
    }

    setSelectedNetworks(selected);
    setNetworks(selected);
    setNetworkError("");
  };

  const handlePlansChange = (selected: Option[]) => {
    if (selected.length > 1) {
      const firstPlan = selected.slice(0, 1);
      setSelectedPlans(firstPlan);
      setPlans(firstPlan);
      setPlanError("Each policy can only have one plan.");
      toast({
        title: "Each policy can only have one plan.",
        variant: "destructive",
      });
      return;
    }

    setSelectedPlans(selected);
    setPlans(selected);
    setPlanError("");
  };

  const updateItem = async (id: string | number): Promise<void> => {
    setNameError(""); setIdError(""); setDateError("");
    setPlanError(""); setNetworkError("");
    if (!policyName.trim() || !policyId.trim() || !startDate || !endDate) {
      if (!policyName.trim()) setNameError("Name required");
      if (startDate > endDate) setDateError("End Date must be after Start Date.");
      return;
    }
    if (selectedPlans.length > 1) {
      setPlanError("Each policy can only have one plan.");
      return;
    }
    if (selectedNetworks.length > 1) {
      setNetworkError("Each policy can only have one network.");
      return;
    }
    if (nameError || idError || dateError) return;
    try {
      const ValuesNetworks = selectedNetworks.length === 1
        ? [selectedNetworks[0].label]
        : [];
      const ValuesPlans = selectedPlans.length === 1
        ? [selectedPlans[0].label]
        : [];
      const updatedPolicy = {
        Policy_name: policyName, Policy_id: policyId,
        Status: isActive ? "Active" : "Inactive",
        Start_date: startDate, End_date: endDate,
        Plan: ValuesPlans, Network: ValuesNetworks, token, id,
      };
      const response = await PolicyAxiosInstance.put(`updatepolicy`, updatedPolicy);
      if (response.data.success) {
        await fetchItems(); setCurrentPage(0); clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "Policy updated successfully." });
      } else {
        const errorMessage = getPolicyErrorMessage({
          response: { data: response.data },
        });
        applyPolicyRelationError(errorMessage);
        toast({ title: "Error", description: errorMessage || "Failed to update Policy.", variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      const errorMessage = getPolicyErrorMessage(error);
      applyPolicyRelationError(errorMessage);
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
    }
  };

  // ✅ Soft Delete
  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await PolicyAxiosInstance.delete(`softDeletePolicy/${id}`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Policy Deleted Successfully." });
      } else {
        setForceId(id); setForceAction("softDelete");
        setForceError(response.data.error || "This policy is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "Failed to delete Policy";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  // ✅ Restore
  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await PolicyAxiosInstance.patch(`restorePolicy/${id}`, { token });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Policy restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore Policy", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore Policy", variant: "destructive" });
    }
  };

  // ✅ Hard Delete (permanent)
  const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
    try {
      const url = force ? `deletePolicy/${id}?force=true` : `deletePolicy/${id}`;
      const response = await PolicyAxiosInstance.delete(url, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceTargetItem(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Policy Permanently Deleted." });
      } else {
        setForceAction("hardDelete");
        setForceError(response.data.error || "This policy is used elsewhere.");
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(error.response?.data?.error || "Failed to delete Policy");
    }
  };

  // ✅ Force Soft Delete
  const forceSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await PolicyAxiosInstance.delete(`softDeletePolicy/${id}?force=true`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceTargetItem(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Policy force Deleted Successfully." });
      } else {
        setForceError(response.data.error || "Failed to force delete Policy");
      }
    } catch (error: any) {
      setForceError(error.response?.data?.error || "Failed to force delete Policy");
    }
  };

  const handleDownload = (item: Result) => {
    const rows = ["policy_name;policy_id;is_active;start_date;end_date;plan;network;"];
    rows.push([
      item.policy_name, item.policy_id,
      item.status ? "Active" : "Inactive",
      item.start_date, item.end_date,
      item.plan.join(","), item.network.join(","),
    ].join(";"));
    const csvContent = rows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Policy${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const [nameError, setNameError] = useState<string>("");
  const [idError, setIdError] = useState<string>("");
  const [dateError, setDateError] = useState<string>("");
  const [planError, setPlanError] = useState<string>("");
  const [networkError, setNetworkError] = useState<string>("");

  const getPolicyErrorMessage = (error: any): string => {
    const backendMessage =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message;

    if (backendMessage === "A policy can only have at most one plan.") {
      return "Each policy can only have one plan.";
    }

    if (backendMessage === "A policy can only have at most one network.") {
      return "Each policy can only have one network.";
    }

    return backendMessage || "An unexpected error occurred.";
  };

  const applyPolicyRelationError = (message: string): void => {
    const normalizedMessage = message.toLowerCase();

    if (normalizedMessage.includes("one plan")) {
      setPlanError(message);
    }

    if (normalizedMessage.includes("one network")) {
      setNetworkError(message);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPolicyName(value);
    if (!value.trim()) { setNameError("Name required"); return; }
    if (value.trim().length < 3) { setNameError("Name must be at least 3 characters long"); return; }
    setNameError("");
  };

  const handleIDChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPolicyId(value);
    const alphanumericRegex = /^[a-zA-Z0-9]+$/;
    if (!alphanumericRegex.test(value.trim())) { setIdError("Policy ID must contain only letters and numbers"); return; }
    if (value.trim().length < 3) { setIdError("Policy ID must be at least 3 characters long"); return; }
    setIdError("");
  };

  const [selectedItem, setSelectedItem] = useState<Result | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  const handleOpenDialog = (item: Result) => { setSelectedItem(item); dialogRef.current?.showModal(); };
const handleCloseDialog = () => { setSelectedItem(null); setShowAllUpdates(false); };
  const columns: ColumnDef<Result>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button variant="ghost" className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ID{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => <span className="font-medium text-center block">{row.original.id}</span>,
    },
    {
      accessorKey: "policy_id",
      header: ({ column }) => (
        <Button variant="ghost" className="font-bold text-white hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Policy ID{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => <span className="font-medium text-center block">{row.getValue("policy_id")}</span>,
    },
    {
      accessorKey: "policy_name",
      header: ({ column }) => (
        <Button variant="ghost" className="font-bold text-white hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Policy Name{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const isDeleted = (row.original as any).is_deleted;
        return (
          <span className={`font-medium text-center block ${isDeleted ? "line-through text-gray-400" : ""}`}>
            {row.getValue("policy_name")}
          </span>
        );
      },
    },
    {
      accessorKey: "start_date",
      header: ({ column }) => (
        <Button variant="ghost" className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Start Date{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const rawDate = row.getValue("start_date");
        const dateValue = rawDate ? String(rawDate) : "";
        const formattedDate = dateValue && !isNaN(Date.parse(dateValue)) ? new Date(dateValue).toLocaleDateString("en-GB") : "";
        return <span className="block text-center font-medium">{formattedDate}</span>;
      },
    },
    {
      accessorKey: "end_date",
      header: ({ column }) => (
        <Button variant="ghost" className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          End Date{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const rawDate = row.getValue("end_date");
        const dateValue = rawDate ? String(rawDate) : "";
        const formattedDate = dateValue && !isNaN(Date.parse(dateValue)) ? new Date(dateValue).toLocaleDateString("en-GB") : "";
        return <span className="block text-center font-medium">{formattedDate}</span>;
      },
    },
    {
      accessorKey: "plan",
      header: ({ column }) => (
        <Button variant="ghost" className="font-bold text-white hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Plan{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const rawPlanData = row.getValue("plan");
        if (!rawPlanData) return <span className="text-gray-400">-</span>;

        const planArray: any[] = Array.isArray(rawPlanData)
          ? rawPlanData
          : typeof rawPlanData === "object"
            ? [rawPlanData]
            : [rawPlanData];

        const normalizedPlans = planArray.map((plan) => ({
          raw: plan,
          name:
            typeof plan === "object"
              ? String(
                  plan.plan_name ??
                    plan.name ??
                    plan.label ??
                    plan.Plan_name ??
                    "",
                )
              : String(plan),
        }));

        const searchTerm = filterName.toLowerCase().trim();
        const hasSearch = searchTerm.length >= 2;
        const matchingPlans = hasSearch
          ? normalizedPlans.filter((plan) =>
              plan.name.toLowerCase().includes(searchTerm),
            )
          : [];

        const hasMatches = hasSearch && matchingPlans.length > 0;
        const displaySource = hasMatches ? matchingPlans : normalizedPlans;
        const displayedPlans = displaySource.slice(0, 2);
        const remainingCount = Math.max(0, displaySource.length - 2);

        return (
          <div className="w-full px-2 py-1">
            <div className="flex flex-wrap items-center justify-center gap-1.5">
              {displayedPlans.map((plan, idx) => {
                const isMatching =
                  hasSearch &&
                  plan.name.toLowerCase().includes(searchTerm);

                return (
                  <span
                    key={`${plan.name}-${idx}`}
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                      isMatching
                        ? "bg-yellow-200 text-yellow-900 border-yellow-400"
                        : "bg-green-100 text-green-800 border-green-200"
                    }`}
                  >
                    {plan.name}
                  </span>
                );
              })}

              {remainingCount > 0 && (
                <button
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedPlans(
                      (hasMatches ? matchingPlans : normalizedPlans).map(
                        (plan, index) => ({
                          value:
                            typeof plan.raw === "object"
                              ? String(
                                  plan.raw.id ??
                                    plan.raw.plan_id ??
                                    plan.raw.value ??
                                    index,
                                )
                              : String(index),
                          label: plan.name,
                        }),
                      ),
                    );
                    setPlanDialogOpen(true);
                  }}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold text-white transition-colors ${
                    hasMatches
                      ? "bg-yellow-600 hover:bg-yellow-700"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  +{remainingCount} more
                </button>
              )}
            </div>

            <div className="mt-1 text-center text-[10px] text-gray-400">
              {hasMatches
                ? `${matchingPlans.length}/${normalizedPlans.length}`
                : `${normalizedPlans.length} total`}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "network",
      header: ({ column }) => (
        <Button variant="ghost" className="font-bold text-white hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Networks{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const rawNetworks = row.getValue("network");
        if (!rawNetworks) return <span className="text-gray-400">-</span>;

        const networkArray: any[] = Array.isArray(rawNetworks)
          ? rawNetworks
          : typeof rawNetworks === "object"
            ? [rawNetworks]
            : [rawNetworks];

        const normalizedNetworks = networkArray.map((network, index) => ({
          id:
            typeof network === "object"
              ? String(network.id ?? network.value ?? index)
              : String(index),
          name:
            typeof network === "object"
              ? String(
                  network.network_name ??
                    network.name ??
                    network.label ??
                    "",
                )
              : String(network),
        }));

        const searchTerm = filterName.toLowerCase().trim();
        const hasSearch = searchTerm.length >= 2;
        const matchingNetworks = hasSearch
          ? normalizedNetworks.filter((network) =>
              network.name.toLowerCase().includes(searchTerm),
            )
          : [];

        const hasMatches = hasSearch && matchingNetworks.length > 0;
        const displaySource = hasMatches
          ? matchingNetworks
          : normalizedNetworks;
        const displayedNetworks = displaySource.slice(0, 2);
        const remainingCount = Math.max(0, displaySource.length - 2);

        return (
          <div className="w-full px-2 py-1">
            <div className="flex flex-wrap items-center justify-center gap-1.5">
              {displayedNetworks.map((network, idx) => {
                const isMatching =
                  hasSearch &&
                  network.name.toLowerCase().includes(searchTerm);

                return (
                  <span
                    key={`${network.id}-${idx}`}
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                      isMatching
                        ? "bg-yellow-200 text-yellow-900 border-yellow-400"
                        : "bg-purple-100 text-purple-800 border-purple-200"
                    }`}
                  >
                    {network.name}
                  </span>
                );
              })}

              {remainingCount > 0 && (
                <button
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedNetworks(
                      (hasMatches
                        ? matchingNetworks
                        : normalizedNetworks
                      ).map((network) => ({
                        value: network.id,
                        label: network.name,
                      })),
                    );
                    setNetworkDialogOpen(true);
                  }}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold text-white transition-colors ${
                    hasMatches
                      ? "bg-yellow-600 hover:bg-yellow-700"
                      : "bg-purple-600 hover:bg-purple-700"
                  }`}
                >
                  +{remainingCount} more
                </button>
              )}
            </div>

            <div className="mt-1 text-center text-[10px] text-gray-400">
              {hasMatches
                ? `${matchingNetworks.length}/${normalizedNetworks.length}`
                : `${normalizedNetworks.length} total`}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <Button variant="ghost" className="font-bold text-white hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Status{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const isActive = item.status === "Active" || item.status === true;
        const isDeleted = (item as any).is_deleted;
        return (
          <div className="flex justify-center">
            <button
              onClick={() => !isDeleted && togglePolicyStatus(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : isActive ? "bg-green-500" : "bg-gray-300"}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isActive && !isDeleted ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },
 {
  id: "view",
  header: () => <Button variant="ghost" className="font-bold text-white hover:bg-white/10 hover:text-white">View</Button>,
  cell: ({ row }) => {
    const item = row.original;
    return (
      <div className="flex justify-center items-center gap-2">
        <button onClick={() => setSelectedItem(item)} className="text-blue-600 hover:text-blue-800 flex justify-center" title="View Details">
          <i className="fa-solid fa-eye text-lg"></i>
        </button>
      </div>
    );
  },
},
  ];

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4" data-qa-page="policy">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-shield-alt"></i>
          </span>
          Policy
        </h5>
      </div>
      <DataTable2
        qaIdPrefix="policy-data-table"
        columns={columns}
        data={items.result}
        searchColumns={[]}
        searchValue={filterName}
        onSearchChange={setFilterName}
searchPlaceholder="Search..."
searchHelpText="Policy ID, Policy Name, Network Name, or Plan Name."
        addButtonLabel="Add New Policy"
        showAddButton={true}
        compactToolbar={true}
        toolbarUpload={<FileUpload fetchItems={fetchItems} />}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        onEdit={handleEdit}
        onDelete={(id) => {
          const item = items.result.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id);
            setForceAction("hardDelete");
            setForceError("");
            setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
        canHardDelete={canManageDeleted}
        onAdd={handleAddClick}
        onEditClick={handleEditClick}
        onDownload={handleDownload}
        isRowDeleted={(row: any) => row.is_deleted === true}
        toolbarExtras={
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <ServerStatusPills
              value={filterStatus}
              onChange={(value) => {
                setFilterStatus(value);
                setCurrentPage(0);
              }}
            />
            <ServerDeletedPills
              value={filterDeleted}
              onChange={(value) => {
                setFilterDeleted(value);
                setCurrentPage(0);
              }}
            />
          </div>
        }
      />




  {/* ─── View Dialog ─────────────────────────────────────────────────────── */}
{Boolean(selectedItem) && selectedItem && (
  <Dialog open={!!selectedItem} onOpenChange={(open) => { if (!open) setSelectedItem(null); }}>
    <DialogContent className="max-w-[780px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)" }}>

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
        <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
            <i className="fa-solid fa-shield-alt" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2, flexWrap: "wrap" }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em" }}>{selectedItem.policy_name}</h2>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: (selectedItem.status === "Active" || selectedItem.status === true) ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: (selectedItem.status === "Active" || selectedItem.status === true) ? "#86efac" : "#fca5a5", border: `1px solid ${(selectedItem.status === "Active" || selectedItem.status === true) ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                {selectedItem.status === "Active" || selectedItem.status === true ? "Active" : "Inactive"}
              </span>
              {(selectedItem as any).is_deleted && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, background: "rgba(239,68,68,0.15)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }}>
                  <i className="fa-solid fa-trash" style={{ fontSize: 10 }} /> Deleted
                </span>
              )}
            </div>
            <p style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0 }}>Policy id: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.policy_id}</span></p>
          </div>
        </div>
      </div>

      {/* Body */}
      <ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
        <div style={{ padding: "20px 28px 16px" }}>

          {/* Basic Info */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
              <i className="fa-solid fa-circle-info" style={{ fontSize: 12, color: "#3b82f6" }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Basic information</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
            {[
              { label: "Policy name", value: selectedItem.policy_name, icon: "fa-shield-alt", color: "#3b82f6" },
              { label: "Policy id", value: `#${selectedItem.policy_id}`, icon: "fa-id-card", color: "#8b5cf6" },
              { label: "Start date", value: selectedItem.start_date ? new Date(selectedItem.start_date).toLocaleDateString("en-GB") : "—", icon: "fa-calendar-days", color: "#10b981" },
              { label: "End date", value: selectedItem.end_date ? new Date(selectedItem.end_date).toLocaleDateString("en-GB") : "—", icon: "fa-calendar-xmark", color: "#f59e0b" },
            ].map((field) => (
              <div key={field.label} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <div style={{ width: 22, height: 22, borderRadius: 6, background: `${field.color}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className={`fa-solid ${field.icon}`} style={{ fontSize: 10, color: field.color }} />
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "capitalize", letterSpacing: "0.05em" }}>{field.label}</span>
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{field.value || "—"}</span>
              </div>
            ))}
          </div>

          <div style={{ height: 1, background: "#e2e8f0", margin: "0 0 20px" }} />

          {/* Networks */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "#faf5ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e9d5ff" }}>
              <i className="fa-solid fa-network-wired" style={{ fontSize: 12, color: "#9333ea" }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Networks</span>
            <span style={{ fontSize: 10, padding: "2px 10px", borderRadius: 9999, background: "#9333ea", color: "#fff", fontWeight: 700 }}>
              {(selectedItem as any).network?.length || 0}
            </span>
          </div>
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", marginBottom: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
            {(selectedItem as any).network && (selectedItem as any).network.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {(selectedItem as any).network.map((net: any, idx: number) => (
                  <span key={idx} style={{ display: "inline-flex", alignItems: "center", padding: "4px 14px", borderRadius: 9999, fontSize: 12, fontWeight: 500, background: "#faf5ff", color: "#7e22ce", border: "1px solid #e9d5ff" }}>
                    {net.network_name}
                  </span>
                ))}
              </div>
            ) : (
              <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>No networks linked to this policy</span>
            )}
          </div>

          <div style={{ height: 1, background: "#e2e8f0", margin: "0 0 20px" }} />

          {/* Plans */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
              <i className="fa-solid fa-file-contract" style={{ fontSize: 12, color: "#16a34a" }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Plans</span>
            <span style={{ fontSize: 10, padding: "2px 10px", borderRadius: 9999, background: "#16a34a", color: "#fff", fontWeight: 700 }}>
              {(selectedItem as any).plan?.length || 0}
            </span>
          </div>
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", marginBottom: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
            {(selectedItem as any).plan && (selectedItem as any).plan.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {(selectedItem as any).plan.map((p: any, idx: number) => {
                  const planName = typeof p === "object" ? p.plan_name || p.name || p.Plan_name || "" : String(p);
                  return (
                    <span key={idx} style={{ display: "inline-flex", alignItems: "center", padding: "4px 14px", borderRadius: 9999, fontSize: 12, fontWeight: 500, background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0" }}>
                      {planName}
                    </span>
                  );
                })}
              </div>
            ) : (
              <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>No plans linked to this policy</span>
            )}
          </div>

          <div style={{ height: 1, background: "#e2e8f0", margin: "0 0 20px" }} />

          {/* Audit Trail */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
              <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit trail</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created by</span>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                  {String(resolveUserName((selectedItem as any).created_by) ?? "?")[0].toUpperCase()}
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span>
              </div>
            </div>
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created on</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                  {(selectedItem as any).createdat ? new Date((selectedItem as any).createdat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                </span>
              </div>
            </div>
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Last updated</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                  {(selectedItem as any).updatedat ? new Date((selectedItem as any).updatedat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                </span>
              </div>
            </div>
          </div>

        {Array.isArray((selectedItem as any).updated_by) && (selectedItem as any).updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginTop: 12 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {(selectedItem as any).updated_by.length} update{(selectedItem as any).updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {((selectedItem as any).updated_by as { id?: number; at?: string }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {String(resolveUserName(entry.id) ?? "?")[0].toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id)}</span>
            {i === 0 && <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>}
          </div>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
            {entry.at ? new Date(entry.at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
          </span>
        </div>
      ))}
      {(selectedItem as any).updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button onClick={() => setShowAllUpdates(v => !v)} style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
            {showAllUpdates ? "Show less ↑" : `+${(selectedItem as any).updated_by.length - 4} more updates ↓`}
          </button>
        </div>
      )}
    </div>
  </div>
)}

          <div style={{ paddingBottom: 8 }} />
        </div>
      </ScrollArea>

      {/* Footer */}
      <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", flexShrink: 0 }}>
        <Button onClick={() => setSelectedItem(null)} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>
          Close Details
        </Button>
      </div>
    </DialogContent>
  </Dialog>
)}

      {/* ─── Plan Dialog ──────────────────────────────────────────────────────── */}
      <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-green-900 flex items-center gap-2">
              <i className="fa-solid fa-list-check"></i>All Plans
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
              <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin', scrollbarColor: '#10b981 #d1fae5' }}>
                {selectedPlans && selectedPlans.map((plan, index) => {
                  const planName = typeof plan === 'object' && plan !== null
                    ? ((plan as any).plan_name || (plan as any).name || (plan as any).label || (plan as any).Plan_name || JSON.stringify(plan))
                    : String(plan);
                  return (
                    <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border border-green-100 hover:border-green-300 transition-all">
                      <div className="flex items-center justify-center w-8 h-8 bg-green-600 text-white rounded-full flex-shrink-0">
                        <i className="fa-solid fa-file-contract text-sm"></i>
                      </div>
                      <span className="text-gray-800 font-medium flex-1">{planName}</span>
                      <span className="text-xs text-gray-500 bg-green-100 px-2 py-1 rounded">#{index + 1}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-3 border-t border-green-200">
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <i className="fa-solid fa-info-circle text-green-600"></i>
                  <span>Total: <strong className="text-green-900">{selectedPlans?.length || 0}</strong> plans</span>
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setPlanDialogOpen(false)} className="bg-green-600 hover:bg-green-700">
              <i className="fa-solid fa-check mr-2"></i>Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Network Dialog ───────────────────────────────────────────────────── */}
      <Dialog open={networkDialogOpen} onOpenChange={setNetworkDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-purple-900 flex items-center gap-2">
              <i className="fa-solid fa-list-check"></i>All Networks
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg p-4 border border-purple-200">
              <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin', scrollbarColor: '#9333ea #e9d5ff' }}>
                {selectedNetworks && selectedNetworks.map((network, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border border-purple-100 hover:border-purple-300 transition-all">
                    <div className="flex items-center justify-center w-8 h-8 bg-purple-600 text-white rounded-full flex-shrink-0">
                      <i className="fa-solid fa-network-wired text-sm"></i>
                    </div>
                    <span className="text-gray-800 font-medium flex-1">{network.label}</span>
                    <span className="text-xs text-gray-500 bg-purple-100 px-2 py-1 rounded">#{index + 1}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-purple-200">
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <i className="fa-solid fa-info-circle text-purple-600"></i>
                  <span>Total: <strong className="text-purple-900">{selectedNetworks?.length || 0}</strong> networks</span>
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setNetworkDialogOpen(false)} className="bg-purple-600 hover:bg-purple-700">
              <i className="fa-solid fa-check mr-2"></i>Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

  {/* ─── Add / Edit Dialog ────────────────────────────────────────────────── */}
<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
<DialogContent
  className="max-w-[720px] max-h-[90vh] flex flex-col p-0 bg-white border-none rounded-2xl"
  onInteractOutside={(event: any) => {
    const target = event.target as HTMLElement;

    if (
      target.closest(".policy-plan-select__menu") ||
      target.closest(".policy-plan-select__menu-portal") ||
      target.closest(".policy-network-select__menu") ||
      target.closest(".policy-network-select__menu-portal")
    ) {
      event.preventDefault();
    }
  }}
  onPointerDownOutside={(event: any) => {
    const target = event.target as HTMLElement;

    if (
      target.closest(".policy-plan-select__menu") ||
      target.closest(".policy-plan-select__menu-portal") ||
      target.closest(".policy-network-select__menu") ||
      target.closest(".policy-network-select__menu-portal")
    ) {
      event.preventDefault();
    }
  }}
  style={{
    boxShadow:
      "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
    overflow: "visible",
  }}
>    <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "22px 28px", position: "relative", overflow: "hidden", borderRadius: "16px 16px 0 0", flexShrink: 0 }}>
      <div style={{ position: "absolute", top: -40, right: -40, width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative", zIndex: 1 }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
          <i className={`fa-solid ${editIndex === null ? "fa-plus" : "fa-pen"}`} />
        </div>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#fff", margin: 0 }}>{editIndex === null ? "Add Policy" : "Edit Policy"}</h2>
          </div>
      </div>
    </div>

    <ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
      <div style={{ padding: "22px 28px 32px" }}>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
            <i className="fa-solid fa-circle-info" style={{ fontSize: 11, color: "#3b82f6" }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Basic Information</span>
        </div>
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginBottom: 18 }}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="policy_name" className="text-xs font-semibold text-slate-500">Policy Name</Label>
              <Input id="policy_name" value={policyName} onChange={handleNameChange} className={`mt-1.5 ${nameError ? "border-red-400 focus-visible:ring-red-300" : ""}`} />
              {nameError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{nameError}</p>}
            </div>
            <div>
              <Label htmlFor="policy_id" className="text-xs font-semibold text-slate-500">Policy ID</Label>
              <Input id="policy_id" value={policyId} onChange={handleIDChange} className={`mt-1.5 ${idError ? "border-red-400 focus-visible:ring-red-300" : ""}`} />
              {idError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{idError}</p>}
            </div>
          </div>
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
            <Label className="text-xs font-semibold text-slate-500">Status</Label>
            <div style={{ display: "flex", gap: 8, marginTop: 8, maxWidth: 260 }}>
              <button type="button" onClick={() => setIsActive(true)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: isActive ? "1px solid #16a34a" : "1px solid #e2e8f0", background: isActive ? "#f0fdf4" : "#fff", color: isActive ? "#15803d" : "#64748b" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Active
              </button>
              <button type="button" onClick={() => setIsActive(false)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: !isActive ? "1px solid #dc2626" : "1px solid #e2e8f0", background: !isActive ? "#fef2f2" : "#fff", color: !isActive ? "#b91c1c" : "#64748b" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Inactive
              </button>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
            <i className="fa-solid fa-calendar-days" style={{ fontSize: 11, color: "#16a34a" }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Duration</span>
        </div>
        <div style={{ background: "#fff", borderRadius: 12, border: `1px solid ${dateError ? "#fca5a5" : "#e2e8f0"}`, padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginBottom: 18 }}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date" className="text-xs font-semibold text-slate-500">Start Date</Label>
              <Input id="start_date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="end_date" className="text-xs font-semibold text-slate-500">End Date</Label>
              <Input id="end_date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1.5" />
            </div>
          </div>
          {dateError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{dateError}</p>}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: "#faf5ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e9d5ff" }}>
            <i className="fa-solid fa-network-wired" style={{ fontSize: 11, color: "#9333ea" }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Networks & Plans</span>
        </div>
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="network" className="text-xs font-semibold text-slate-500">Network</Label>
              <div className="mt-1.5">
                <PolciyNetworksSelector
                  onChange={handleNetwrksChange}
                  clear={clearNetworks}
                  value={networks}
                />
              </div>
              {networkError && (
                <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                  <i className="fa-solid fa-circle-exclamation text-[10px]" />
                  {networkError}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="plan" className="text-xs font-semibold text-slate-500">Plan</Label>
              <div className="mt-1.5">
                <PolciyPlansSelector
                  onChange={handlePlansChange}
                  clear={clearPlans}
                  value={plans}
                />
              </div>
              {planError && (
                <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                  <i className="fa-solid fa-circle-exclamation text-[10px]" />
                  {planError}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </ScrollArea>

    <div style={{ padding: "16px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", justifyContent: "flex-end", gap: 10, borderRadius: "0 0 16px 16px", flexShrink: 0 }}>
      <Button variant="outline" onClick={() => { clear(); setEditIndex(null); setIsDialogOpen(false); }} style={{ borderRadius: 10, fontWeight: 600 }}>Cancel</Button>
      <Button onClick={() => { if (editIndex === null) addItem(); else updateItem(editIndex); }} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 24px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>
        <i className={`fa-solid ${editIndex === null ? "fa-plus" : "fa-check"} mr-2`}></i>
        {editIndex === null ? "Add Policy" : "Update Policy"}
      </Button>
    </div>
  </DialogContent>
</Dialog>

      {/* ─── Force / Hard Delete Dialog ───────────────────────────────────────── */}
      {forceDialogOpen && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
              </div>

              {/* Hard Delete */}
              {forceAction === "hardDelete" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Permanent Delete</h3>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.6 }}>
                    This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> the policy.<br />This action <strong>cannot be undone</strong>.
                  </p>
                  {forceError && (
                    <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                        <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                      </div>
                    </div>
                  )}
                 <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
  <button
    onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
    style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
    Cancel
  </button>
  <button
    onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
    style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
    <i className="fa-solid fa-bolt mr-2"></i>Force Delete Anyway
  </button>
</div>
                </>
              )}

              {/* Soft Delete / Status — مستخدم في مكان ثاني */}
              {(forceAction === "softDelete" || forceAction === "status") && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
                    {forceAction === "status" ? "Status Change Blocked" : "Policy Is In Use"}
                  </h3>
                  <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                      <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                    Do you want to <strong style={{ color: "#dc2626" }}>force {forceAction === "status" ? "update status" : "delete"}</strong> anyway?
                  </p>
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceTargetItem(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
                    <button
                      onClick={() => {
                        if (!forceId) return;
                        if (forceAction === "softDelete") forceSoftDelete(forceId);
                        else if (forceAction === "status" && forceTargetItem) togglePolicyStatus(forceTargetItem, true);
                      }}
                      style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt mr-2"></i>Force {forceAction === "status" ? "Update" : "Delete"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default Policy;